import * as React from 'react';
import {Link} from 'react-router-dom';

import classnames from 'classnames';

import {Routing} from 'nutshell-core/routing';

import styles from './linked-entity.module.css';

const typeMap = {
    accounts: 'company',
    contacts: 'person',
    leads: 'lead',
    activityExternalAttendees: 'person',
} as const;

const STYLED_PILL_TYPES = {
    leads: 'container-leads',
    greenLeads: 'container-leads--green',
    users: 'container-users',
    teams: 'container-teams',
    contacts: 'container-contacts',
    accounts: 'container-accounts',
    activityExternalAttendees: 'container-activityExternalAttendees',
} as const;

type Props = {
    name?: string | React.ReactElement<any>;
    type: string;
    isEnabled?: boolean;
    htmlUrl?: string | null | undefined;
    htmlUrlPath?: string;
    isLinkable: boolean;
    shouldClientRoute?: boolean;
    stopPropagation?: boolean;
    useEntityColor?: boolean;
    allowWrap?: boolean;
    displayInline?: boolean;
    greenLeads?: boolean;
    email?: string;
    phone?: string;
    onCustomClick?: (e: Event) => void;
    newTab?: boolean;
};

/**
 * A textual link to an entity, styled with the appropriate color for the entity type.
 */
export class LinkedEntity extends React.PureComponent<Props> {
    static defaultProps = {
        isLinkable: true,
        useEntityColor: true,
        isEnabled: true,
    };

    render() {
        const hasName =
            (this.props.name && typeof this.props.name === 'object') ||
            (this.props.name && typeof this.props.name === 'string' && this.props.name.length);

        const fallback = this.props.email || this.props.phone;

        let entityName;
        if (hasName) {
            entityName = this.props.name;
        } else if (fallback) {
            entityName = fallback;
        } else {
            entityName = (
                <span className={styles.nameless}>
                    {/* @ts-expect-error */}
                    Unnamed {typeMap[this.props.type]}
                </span>
            );
        }

        let containerStyleTypeKey =
            // @ts-expect-error
            this.props.useEntityColor && STYLED_PILL_TYPES[this.props.type]
                ? // @ts-expect-error
                  STYLED_PILL_TYPES[this.props.type]
                : 'container-default';

        if (this.props.type === 'leads') {
            containerStyleTypeKey = this.props.greenLeads
                ? STYLED_PILL_TYPES.greenLeads
                : STYLED_PILL_TYPES.leads;
        }

        // @ts-expect-error
        const containerStyleType = styles[containerStyleTypeKey];

        const link =
            (this.props.htmlUrl && Routing.hasWorkingUrl() && this.isClientRoutable()) ||
            this.props.htmlUrlPath ? (
                // @ts-expect-error
                <Link
                    // @ts-expect-error
                    to={
                        this.props.htmlUrl
                            ? new URL(this.props.htmlUrl).pathname
                            : this.props.htmlUrlPath
                    }
                    // @ts-expect-error
                    onClick={this.handleClick}
                    target={this.props.newTab ? '_blank' : undefined}
                    rel={this.props.newTab ? 'noreferrer noopener' : undefined}
                    // ESC-331: prevent browsers from capturing touch swipes
                    // as a native link-drag gesture, which would block
                    // vertical scrolling in touch-device list views.
                    draggable={false}
                >
                    {entityName}
                </Link>
            ) : (
                <a
                    className='pointer-cursor'
                    // @ts-expect-error
                    onClick={this.handleClick}
                    // @ts-expect-error
                    href={this.props.onCustomClick ? undefined : this.props.htmlUrl}
                    target={this.props.newTab ? '_blank' : undefined}
                    rel={this.props.newTab ? 'noreferrer noopener' : undefined}
                    // ESC-331: prevent browsers from capturing touch swipes
                    // as a native link-drag gesture, which would block
                    // vertical scrolling in touch-device list views.
                    draggable={false}
                >
                    {entityName}
                </a>
            );

        const Tag = this.props.displayInline ? 'span' : 'div';

        const className = classnames({
            truncate: !this.props.allowWrap,
            'word-break': this.props.allowWrap,
            strikethrough: !this.props.isEnabled,
        });

        return (
            <Tag className={classnames(className, containerStyleType)}>
                {this.props.isLinkable &&
                (this.props.htmlUrl || this.props.htmlUrlPath || this.props.onCustomClick)
                    ? link
                    : entityName}
            </Tag>
        );
    }

    isClientRoutable = () => {
        return (
            (this.props.type === 'leads' ||
                this.props.type === 'contacts' ||
                this.props.type === 'accounts') &&
            this.props.shouldClientRoute
        );
    };

    handleClick = (e: Event) => {
        if (this.props.stopPropagation) e.stopPropagation();
        const customClickHandler = this.props.onCustomClick;
        if (customClickHandler) {
            customClickHandler(e);
        }
    };
}
